import { defineComponent as _defineComponent } from 'vue'
import { computed, toRefs } from 'vue';
import SortableColumnHeader from './SortableColumnHeader.vue';
import { useAuth } from '@/composables';
import { ENTITY_PERMISSIONS, getEntityPermissions } from '@/lib/constants/entityPermissions';
import type { EntityKey, EntityPermission } from '@/lib/constants/entityPermissions';
import type { ColumnInfo, ElementUISlotScope } from '@/lib/types';
import { Order } from '@/mixins/filterHandler/lib';

// Definizione props con TypeScript
interface Props {
	entity?: EntityKey | null;
	codeField?: string;
	descriptionField?: string;
	sortable?: boolean;
	roles?: string[];
	link?: string;
	orderByField?: string;
	// Corretto tipo Order per compatibility con SortableColumnHeader
	dataOrder?: Order | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ElTableColumnCodeDescription',
  props: {
    entity: { default: null },
    codeField: { default: 'code' },
    descriptionField: { default: 'name' },
    sortable: { type: Boolean, default: false },
    roles: { default: () => [] },
    link: { default: '' },
    orderByField: { default: 'code' },
    dataOrder: { default: null }
  },
  emits: ['orderChanged'],
  setup(__props: any, { emit }) {

const props = __props as { entity: EntityKey | null, codeField: string, descriptionField: string, sortable: boolean, roles: string[], link: string, orderByField: string, dataOrder: Order | null };



// Auth composable
const { isInRoles } = useAuth();

// Validazione entity in development
if (props.entity && !ENTITY_PERMISSIONS[props.entity]) {
	console.error(
		`Invalid entity "${props.entity}". Available entities:`,
		Object.keys(ENTITY_PERMISSIONS),
	);
}

// Definizione emits


// Handler per order-change event
const handleOrderChange = (property: string, operator: any) => {
	emit('orderChanged', property, operator);
};

// Refs per props (per accesso nel template)
const { sortable } = toRefs(props);

// Computed - Configurazione dell'entità (se specificata)
const entityConfig = computed((): EntityPermission | {} => {
	if (props.entity) {
		try {
			return getEntityPermissions(props.entity);
		} catch (error) {
			console.error('ElTableColumnCodeDescription:', error.message);
			return {};
		}
	}
	return {};
});

// Computed - Ruoli computati: priorità alle props esplicite, fallback alla configurazione entità
const computedRoles = computed(() => {
	return props.roles.length > 0
		? props.roles
		: (entityConfig.value as EntityPermission)?.roles || [];
});

// Computed - Link computato: priorità alle props esplicite, fallback alla configurazione entità
const computedLink = computed(() => {
	return props.link || (entityConfig.value as EntityPermission)?.link || '';
});

// Computed - Percorso computato: usa il path dall'entityConfig, fallback a 'system'
const computedPath = computed(() => {
	return (entityConfig.value as EntityPermission)?.path || 'system';
});

// Computed - URL completo per i link
const computedUrl = computed(() => {
	return `/${computedPath.value}/${computedLink.value}`;
});

// Computed - Permessi
const hasPermission = computed(() => {
	return computedRoles.value.length > 0 ? isInRoles([...computedRoles.value]) : true;
});

// Helper functions per costruire URL (per evitare template literals nel template)
const buildCodeUrl = (column: any, row: any): string => {
	const code = evaluateCode(column, row);
	return `${computedUrl.value}?${props.codeField}=${code}`;
};

const buildDescriptionUrl = (column: any, row: any): string => {
	const description = evaluateDescription(column, row);
	return `${computedUrl.value}?${props.descriptionField}=${description}`;
};

// Utility function per risolvere proprietà nested negli oggetti
const _resolveObjProp = (path: string, obj: any): any => {
	return path.split('.').reduce((prev, curr) => {
		return prev ? prev[curr] : null;
	}, obj || null);
};

// Methods - Valuta il codice da una colonna/riga
const evaluateCode = (column: any, row: any): string => {
	const obj = _resolveObjProp(column.property, row);
	if (obj) {
		const field = obj[props.codeField];
		return field && field !== '' ? field : 'n/a';
	}
	return 'n/a';
};

// Methods - Valuta la descrizione da una colonna/riga
const evaluateDescription = (column: any, row: any): string => {
	const obj = _resolveObjProp(column.property, row);
	if (obj) {
		const field = obj[props.descriptionField];
		return field && field !== '' ? field : 'n/a';
	}
	return 'n/a';
};

// Methods - Verifica se ha una descrizione valida (diversa dal codice)
const hasValidDescription = (column: any, row: any): boolean => {
	const obj = _resolveObjProp(column.property, row);
	if (obj) {
		const code = obj[props.codeField];
		const description = obj[props.descriptionField];
		if (code !== description) {
			return true;
		}
	}
	return false;
};

// Methods - Verifica se ha un codice valido
const hasValidCode = (column: any, row: any): boolean => {
	const code = evaluateCode(column, row);
	return code && code !== 'n/a' && code !== '';
};

// Methods - Informazioni colonna per SortableColumnHeader
const _columnInfo = (column: ElementUISlotScope['column']): ColumnInfo => {
	return {
		column: {
			property: `${column.property}.${props.orderByField}`,
			label: column.label,
		},
	};
};

return { __sfc: true,props, isInRoles, emit, handleOrderChange, sortable, entityConfig, computedRoles, computedLink, computedPath, computedUrl, hasPermission, buildCodeUrl, buildDescriptionUrl, _resolveObjProp, evaluateCode, evaluateDescription, hasValidDescription, hasValidCode, _columnInfo, SortableColumnHeader }
}

})